import { IServerSideGetRowsParams } from '@ag-grid-enterprise/all-modules';
import { useParams } from '@reach/router';
import React from 'react';
import { useTranslation } from 'react-i18next';

import PageContent from '../../../../../components/Layout/PageContent';
import TableGrid from '../../../../../components/TableGrid';
import TablePagination from '../../../../../components/TableGrid/Pagination';
import { useTableGridApiState } from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { fetchCompanies } from '../../../../../core/api/benchmark';
import { CONSTANTS } from '../../../../../core/config/constants';
import { BenchmarkCompanyView } from '../../../../../generated/model/benchmarkCompanyView';
import { useErrorsDispatch } from '../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch, useBenchmarkState } from '../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../Context/BenchmarkContext.types';
import { TabNamesEnum } from '../BenchmarkTabs';
import CompaniesMatrixTableColumns from './CompaniesMatrixTableColumns';

const CompaniesMatrixTable: React.FC = () => {
  const { t } = useTranslation();
  const { pagination } = useBenchmarkState();
  const columns = CompaniesMatrixTableColumns(t, onCellClicked);
  const dispatchErrors = useErrorsDispatch();
  const { tableGridApi } = useTableGridApiState();
  const dispatchBenchmark = useBenchmarkDispatch();
  const { benchmarkId } = useParams();

  // define as function, because function expression won't be hoisted
  function onCellClicked(data: BenchmarkCompanyView) {
    if (data && data?.companyInfo?.id) {
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: data?.companyInfo.id,
      });
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
        payload: TabNamesEnum.MANUAL,
      });
    }
  }

  return (
    <PageContent isPageHeightAuto={false}>
      <TableGrid
        columnsStorageId={CONSTANTS.COLUMNS_STORAGE.BENCHMARKING_MATRIX_STORAGE_ID}
        translationKey="companies:resultTable"
        columns={columns}
        getDatasource={getDatasource()}
        rowSelection={false}
        suppressTableContextProvider={true}
        originalProps={{
          context: {
            pagination,
            benchmarkId,
            dispatchBenchmark,
            dispatchErrors,
          },
          headerHeight: CONSTANTS.TABLE_HEADER - 1,
          rowModelType: 'serverSide',
          pagination: true,
          paginationPageSize: pagination.size,
          cacheBlockSize: pagination.size,
          maxBlocksInCache: 1,
          suppressPaginationPanel: true,
          rowSelection: 'multiple',
          rowMultiSelectWithClick: true,
          suppressRowClickSelection: true,
          defaultColDef: {
            sortable: false,
            resizable: true,
            flex: 1,
            minWidth: 100,
            menuTabs: [],
          },
        }}
      >
        {pagination.totalElements > 0 ? (
          <TablePagination
            gridApi={tableGridApi}
            pagination={pagination}
            onChange={(value: number) => {
              dispatchBenchmark({
                type: BenchmarkContextActionTypes.SetPagination,
                payload: { ...pagination, page: value },
              });
            }}
          />
        ) : null}
      </TableGrid>
    </PageContent>
  );
};

export function getDatasource() {
  return {
    getRows: async (params: IServerSideGetRowsParams) => {
      // quick fix to get provided context params
      const model: any = params.api.getModel();
      const {
        pagination,
        benchmarkId,
        companyFilter,
        dispatchBenchmark,
        dispatchErrors,
      } = model.getContext().getBean('gridOptions').context;
      let totalPages = 0;
      let totalElements = 0;

      const requestParams = {
        page: pagination.page > 0 ? pagination.page - 1 : 0,
        size: pagination.size,
        ...(companyFilter ? { name: companyFilter } : {}),
      };

      try {
        const response = await fetchCompanies(benchmarkId, requestParams);
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetIsLoadingState,
          payload: true,
        });

        if (response) {
          // supply rows for requested block to grid
          const result = response?.searchResult?.content ?? [];
          if (result.length <= 0) {
            // if response has no rows display noRowsOverlay
            params.api.showNoRowsOverlay();
            params.successCallback([], 0);
          } else {
            params.api.hideOverlay();
            totalElements = response?.searchResult?.totalElements || 0;
            totalPages = response?.searchResult?.totalPages || 0;
            params.successCallback(result, totalElements);
            dispatchBenchmark({
              type: BenchmarkContextActionTypes.SetBenchmarkCompanies,
              payload: {
                companies: result,
                benchwiseState: response?.benchwiseState,
              },
            });
            // TODO: refactor to use one request to BE instead of two
            if (params.request.startRow !== requestParams.page * pagination.size) {
              params.api.paginationGoToPage(requestParams.page);
            }
          }
        } else {
          // inform grid request failed
          params.failCallback();
        }
      } catch (error) {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
        // failed request
        params.api.showNoRowsOverlay();
        params.successCallback([], 0);
      } finally {
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetPagination,
          payload: {
            ...pagination,
            totalPages,
            totalElements,
          },
        });
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetIsLoadingState,
          payload: false,
        });
      }
    },
  };
}

export default CompaniesMatrixTable;
